import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Send, ArrowLeft, CheckCircle2, Users, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import Seo from "../components/Seo";

const MINISTRY_OPTIONS = [
  "Hospitality Team", "Worship Team", "1st Sunday Choir", "Youth & Children's Ministry",
  "Outreach Team", "Media / Tech Team", "Singles Ministry", "Married Couples Ministry",
  "Bible Book Club", "Finance Team", "Mother's Board", "Deacons Board",
  "Intercessory Team", "Pastor's Aid",
];

const INITIAL = {
  name: "", email: "", phone: "", address: "", birthday: "",
  family_members: "", is_baptized: false, salvation_date: "",
  wants_baptism: false, interests: [], notes: "",
};

export default function NewMember() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const inputCls = "w-full px-4 py-3 rounded-lg bg-[#050505] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric/30 transition";

  const toggleInterest = (m) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(m)
        ? f.interests.filter((x) => x !== m)
        : [...f.interests, m],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
      });
      if (form.is_baptized === false) payload.is_baptized = false;
      if (form.wants_baptism === false) payload.wants_baptism = false;
      payload.interests = form.interests;
      await api.post("/new-members", payload);
      setDone(true);
      toast.success("Welcome to the family! We'll be in touch.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main id="main-content" className="bg-[#050505] text-white min-h-[70vh] flex items-center" data-testid="newmember-success-page">
        <Seo title="Welcome" path="/new-members" />
        <div className="max-w-2xl mx-auto px-5 lg:px-10 py-24 text-center">
          <div className="w-20 h-20 rounded-full bg-electric/20 flex items-center justify-center mx-auto mb-6 ring-1 ring-electric/40">
            <Heart className="w-10 h-10 text-electric fill-electric" />
          </div>
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">Welcome Home</div>
          <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-5">
            You belong <span className="italic text-electric">here.</span>
          </h1>
          <p className="text-zinc-300 leading-relaxed mb-8">
            Thank you for taking this step, {form.name.split(" ")[0]}. Someone from our team will reach out within a few days to welcome you personally and help connect you to the ministries that fit your gifts.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 bg-electric text-black px-6 py-3 rounded-full font-bold hover:bg-[#4ade80] transition" data-testid="newmember-home">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#050505] text-white" data-testid="newmember-page">
      <Seo
        title="Next Steps"
        description="Take your next step at The Push Center — Portsmouth, VA. Connect with us and find your place in the family."
        path="/new-members"
      />

      <section className="relative pt-20 pb-12 lg:pt-32 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/congregation.jpg" alt="" className="w-full h-full object-cover opacity-70 bg-zoom" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/20" />
          <div className="absolute inset-0 lightning-grid opacity-30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-5">
            Next Steps
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-4xl">
            Take your <span className="italic text-electric text-pulse">next step.</span>
          </h1>
          <p className="mt-7 text-lg text-zinc-300 max-w-2xl">
            Welcome to The Push Center family. Fill out this form and we'll personally reach out to help you find your place, connect with a ministry, and answer any questions.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-12">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { icon: Heart, label: "Belong", desc: "You're family from day one" },
            { icon: Users, label: "Connect", desc: "Meet your spiritual home group" },
            { icon: Sparkles, label: "Grow", desc: "Discover your gifts in ministry" },
          ].map((c, i) => (
            <div key={i} className="p-6 rounded-2xl border border-white/5 bg-[#0a0a0a]" data-testid={`nm-perk-${i}`}>
              <c.icon className="w-6 h-6 text-electric mb-4" />
              <div className="font-display font-bold text-lg mb-1">{c.label}</div>
              <div className="text-sm text-zinc-400">{c.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 lg:px-10 pb-32">
        <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">
          New Member Form
        </div>
        <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter mb-10">
          Tell us about <span className="italic text-electric">you.</span>
        </h2>

        <form onSubmit={submit} className="space-y-6 p-8 lg:p-10 rounded-2xl bg-[#0a0a0a] border border-white/5" data-testid="newmember-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Full Name">
              <input className={inputCls} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="nm-name" />
            </Field>
            <Field label="Email">
              <input type="email" className={inputCls} required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="nm-email" />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Phone">
              <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="nm-phone" />
            </Field>
            <Field label="Birthday">
              <input type="date" className={inputCls} value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} data-testid="nm-birthday" />
            </Field>
          </div>

          <Field label="Address">
            <input className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, City, State, Zip" data-testid="nm-address" />
          </Field>

          <Field label="Family Members (names & ages of spouse / kids — optional)">
            <textarea rows={3} className={inputCls} value={form.family_members} onChange={(e) => setForm({ ...form, family_members: e.target.value })} placeholder="e.g., Spouse: Jane (35), Kids: Mark (8), Lily (5)" data-testid="nm-family" />
          </Field>

          <div className="p-5 rounded-xl bg-[#050505] border border-white/5 space-y-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-electric font-bold">Your Walk</div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.is_baptized} onChange={(e) => setForm({ ...form, is_baptized: e.target.checked })} className="w-4 h-4 accent-[#22C55E]" data-testid="nm-baptized" />
              <span className="text-zinc-200">I am already baptized</span>
            </label>
            {form.is_baptized && (
              <Field label="Salvation Date (optional)">
                <input type="date" className={inputCls} value={form.salvation_date} onChange={(e) => setForm({ ...form, salvation_date: e.target.value })} data-testid="nm-salvation" />
              </Field>
            )}
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.wants_baptism} onChange={(e) => setForm({ ...form, wants_baptism: e.target.checked })} className="w-4 h-4 accent-[#22C55E]" data-testid="nm-wants-baptism" />
              <span className="text-zinc-200">I would like to be baptized</span>
            </label>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-3">Areas of Interest <span className="text-zinc-600 normal-case font-normal">— select all that apply</span></div>
            <div className="grid sm:grid-cols-2 gap-2">
              {MINISTRY_OPTIONS.map((m) => {
                const active = form.interests.includes(m);
                return (
                  <button
                    type="button"
                    key={m}
                    onClick={() => toggleInterest(m)}
                    className={`px-4 py-2.5 rounded-lg border text-sm font-medium text-left transition ${
                      active
                        ? "border-electric bg-electric/10 text-electric"
                        : "border-white/10 bg-[#050505] text-zinc-300 hover:border-electric/40"
                    }`}
                    data-testid={`nm-interest-${m.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  >
                    {active ? "✓ " : ""}{m}
                  </button>
                );
              })}
            </div>
          </div>

          <Field label="Notes / Questions (optional)">
            <textarea rows={4} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything else you'd like us to know?" data-testid="nm-notes" />
          </Field>

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-electric text-black px-7 py-4 rounded-full font-bold tracking-wide hover:bg-[#4ade80] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60"
            data-testid="nm-submit"
          >
            <Heart className="w-4 h-4" />
            {submitting ? "Submitting..." : "Join The Push Family"}
          </button>
        </form>
      </section>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">{label}</label>
      {children}
    </div>
  );
}
